'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ScrollArea, Text, Collapse, UnstyledButton, Group, Box } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { navItems, getNavItemsWithT, NavItem } from './navItems';
import { useTranslation } from 'react-i18next';
import classes from './Sidebar.module.css';

export type SidebarProps = {
  onNavigate?: () => void;
  forceExpanded?: boolean;
};

export function Sidebar({ onNavigate, forceExpanded = false }: SidebarProps) {
  const pathname = usePathname() || '/';
  const { t, ready } = useTranslation();
  const localized = React.useMemo(() => (ready ? getNavItemsWithT(t) : navItems), [t, ready]);

  // auto-open groups that contain the current pathname
  const [openedGroups, setOpenedGroups] = React.useState<Record<string, boolean>>({});
  React.useEffect(() => {
    const next: Record<string, boolean> = { ...openedGroups };
    (localized || navItems).forEach((it: NavItem) => {
      const children = it.children;
      if (children && children.some((c) => c.href === pathname)) {
        next[it.id] = true;
      }
    });
    setOpenedGroups(next);
  }, [pathname, localized]);

  const topItems = (localized || navItems).filter(
    (it: NavItem) => !['settings', 'help'].includes(it.id)
  );
  const bottomItems = (localized || navItems).filter((it: NavItem) =>
    ['settings', 'help'].includes(it.id)
  );

  return (
    <aside
      className={`${classes.appSidemenu} ${forceExpanded ? classes.appSidemenuForceExpanded : ''}`}
      aria-label="Main navigation"
    >
      <div className={classes.sidemenuInner}>
        {/* collapse removed - no toggle displayed */}
        <div className={classes.scrollAreaWrapper}>
          <ScrollArea h="100%">
            <nav className={classes.sidemenunavTop} aria-label="Primary">
              {topItems.map((it: NavItem) => {
                const children = it.children;
                const Icon = it.Icon;

                if (children && children.length > 0) {
                  const parentOpen = !!openedGroups[it.id];
                  return (
                    <div key={it.id} className={classes.navgroup}>
                      <div className={classes.navgroupheading}>
                        <UnstyledButton
                          className={classes.navgrouptoggle}
                          onClick={() => setOpenedGroups((s) => ({ ...s, [it.id]: !s[it.id] }))}
                          aria-expanded={parentOpen}
                          title={it.label}
                        >
                          <Group gap="xs" align="center" wrap="nowrap" w="100%">
                            {Icon ? <Icon size={16} /> : null}
                            <Text size="sm">{it.label}</Text>
                            <IconChevronRight
                              size={16}
                              style={{
                                marginLeft: 'auto',
                                transform: parentOpen ? 'rotate(-90deg)' : 'none',
                                transition: 'transform 120ms ease',
                              }}
                            />
                          </Group>
                        </UnstyledButton>
                      </div>

                      <Collapse in={parentOpen}>
                        {children.map((child: NavItem) => {
                          const ChildIcon = child.Icon;
                          const isActive = pathname === child.href;
                          return child.href ? (
                            <Link
                              key={child.id}
                              href={child.href}
                              className={`${classes.navsubitem} ${isActive ? classes.navitemActive : ''}`}
                              onClick={() => onNavigate && onNavigate()}
                              aria-current={isActive ? 'page' : undefined}
                              title={child.label}
                            >
                              <Group gap="xs" align="center" wrap="nowrap">
                                {ChildIcon ? <ChildIcon size={14} /> : null}
                                <Text size="sm">{child.label}</Text>
                              </Group>
                            </Link>
                          ) : null;
                        })}
                      </Collapse>
                    </div>
                  );
                }

                const isActive = pathname === it.href;
                if (it.href) {
                  return (
                    <Link
                      key={it.id}
                      href={it.href}
                      className={`${classes.navitem} ${isActive ? classes.navitemActive : ''}`}
                      onClick={() => onNavigate && onNavigate()}
                      aria-current={isActive ? 'page' : undefined}
                      title={it.label}
                    >
                      <Group gap="xs" align="center" wrap="nowrap">
                        {Icon ? <Icon size={16} /> : null}
                        <Text size="sm">{it.label}</Text>
                      </Group>
                    </Link>
                  );
                }

                return (
                  <Box
                    key={it.id}
                    className={`${classes.navheading} ${isActive ? classes.navitemActive : ''}`}
                    aria-label={it.ariaLabel}
                    component="div"
                    title={it.label}
                  >
                    <Group gap="xs" align="center" wrap="nowrap">
                      {Icon ? <Icon size={16} /> : null}
                      <Text size="sm" className={classes.navlabel} component="span">
                        {it.label}
                      </Text>
                    </Group>
                  </Box>
                );
              })}
            </nav>
          </ScrollArea>
        </div>

        <nav className={classes.sidemenunavBottom} aria-label="Secondary">
          {bottomItems.map((it: NavItem) => {
            const Icon = it.Icon;
            const isActive = pathname === it.href;
            return it.href ? (
              <Link
                key={it.id}
                href={it.href}
                className={`${classes.navitem} ${isActive ? classes.navitemActive : ''}`}
                onClick={() => onNavigate && onNavigate()}
                aria-current={isActive ? 'page' : undefined}
                title={it.label}
              >
                <Group gap="xs" align="center" wrap="nowrap">
                  {Icon ? <Icon size={16} /> : null}
                  <Text size="sm">{it.label}</Text>
                </Group>
              </Link>
            ) : null;
          })}
        </nav>
      </div>
    </aside>
  );
}

export default Sidebar;
